import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Environment } from '../environment/environment';
import { HttpClient } from '@angular/common/http';
import { Response } from '../model/response';
import { Observable, throwError, forkJoin } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { catchError } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { InfoService } from './info.service';
import { User } from '../model/user';
import { UserEventRole } from '../enum/user.event.role';
import { LoginResponse } from '../model/login.response';
import { LoginHistory } from '../model/login.history';
import { plainToClass } from 'class-transformer';
import { EventsService } from './events.service';
import { NotificationService } from './notification.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  constructor(
    private jwtHelper: JwtHelperService, private http: HttpClient, public router: Router, private infoService: InfoService,
    private eventsService: EventsService, private notificationService: NotificationService
  ) { }

  isAuthenticated(): boolean {
    let validToken = false;

    try {
      validToken = !this.jwtHelper.isTokenExpired(localStorage.getItem('token'));
    } catch (error) {
      validToken = false;
    }

    return validToken;
  }

  signOut(redirect = true): void {
    this.infoService.clearProxy();
    localStorage.clear();
    this.resetRoleCaches();
    this.notificationService.clearScheduledMaintenanceWarning();

    if (redirect) {
      // RELOAD de verdade, não `router.navigateByUrl('/')`.
      //
      // Os caches de papel abaixo usam `shareReplay(1)` permanente, apoiados na
      // premissa de que sair da conta recarrega a SPA. Só que a navegação por
      // rota NÃO recarrega nada: o AuthService, a navbar e qualquer componente
      // vivo sobreviviam à troca de conta com o estado do usuário ANTERIOR.
      // Era isso que deixava "Admin" no menu de um usuário comum (e o inverso:
      // o item não aparecia para quem acabara de entrar como admin) até um F5
      // manual — issue #2375.
      //
      // Trocar por um reload torna VERDADEIRA a premissa que o código já
      // documentava, e zera de uma vez todo estado de sessão em memória,
      // inclusive o que não está mapeado aqui.
      window.location.href = '/';
    }
  }

  /** Descarta os caches de papel por sessão. Chamado no signOut ANTES do
   *  desvio de fluxo, porque `signOut(false)` (o caminho de 401 em
   *  `error.service`) não recarrega a página e mesmo assim precisa largar o
   *  estado do usuário que saiu. */
  private resetRoleCaches(): void {
    this.cachedIsAdmin$ = undefined;
    this.cachedIsChair.clear();
    this.cachedIsUserEvent.clear();
  }

  signIn(data: { login: string, password: string }): Observable<LoginResponse> {
    const params: { email?: string, id?: number, password: string } = {
      password: data.password
    };

    const loginIsNumber = !isNaN(Number(data.login));
    if (loginIsNumber) {
      params.id = Number(data.login);
    } else {
      params.email = data.login;
    }
    return this.http.post<Response>(Environment.urls.API + '/core/auth/login/', params)
    .pipe(map((res: Response) => ({
      token: res.data.access,
      refresh: res.data.refresh,
      user: plainToClass(User, res.data.user as User)
    })));
  }

  refreshToken(data: { refresh: string }): Observable<Response> {
    return this.http.post<Response>(Environment.urls.API + '/core/auth/token/refresh/', data);
  }

  passwordReset(data: { uid: string, token: string, newPassword1: string, newPassword2: string }): Observable<object> {
    return this.http.post<object>(Environment.urls.API + '/core/auth/password/reset/confirm/', data);
  }

  requestPasswordReset(data: { login: string }): Observable<object> {
    const params: { email?: string, id?: number } = {};

    const loginIsNumber = !isNaN(Number(data.login));
    if (loginIsNumber) {
      params.id = Number(data.login);
    } else {
      params.email = data.login;
    }

    return this.http.post<object>(Environment.urls.API + '/core/auth/password/reset/', params);
  }

  passwordChange(data: { newPassword1: string, newPassword2: string, oldPassword: string }): Observable<Response> {
    return this.http.post<Response>(Environment.urls.API + '/core/auth/password/change/', data)
      .pipe(catchError((e: any) => throwError(e.error)));
  }

  userIsChair(event): Observable<boolean> {
    return this.eventsService.getEventChairs(event).pipe(
      map(userRoles => <boolean>!!userRoles.find(e =>
        e.user.id === this.infoService.user.id
      ))
    );
  }

  // ─── Permission checks: session-wide cache via `shareReplay(1)` ────
  // O status (superuser / chair / role no evento) NÃO muda durante a
  // sessão — então `shareReplay(1)` permanente é o ideal: a 1ª chamada
  // dispara o HTTP, todas as outras (mesmo após o request concluir)
  // recebem o valor cacheado SEM novo fetch. Cobre tanto a corrida
  // (vários componentes pedindo em paralelo) quanto chamadas sequenciais.
  // Invalidados no `signOut` por `resetRoleCaches()`, e o reload que o signOut
  // dispara zera o resto. ANTES o comentário aqui afirmava que sair da conta
  // recarregava a SPA — não recarregava (era `router.navigateByUrl`), e o
  // cache vazava entre contas (issue #2375).
  private cachedIsAdmin$?: Observable<boolean>;
  private cachedIsChair = new Map<number, Observable<boolean>>();
  private cachedIsUserEvent = new Map<string, Observable<boolean>>();

  directUserIsAdmin(): Observable<boolean> {
    if (!this.cachedIsAdmin$) {
      this.cachedIsAdmin$ = this.http.get<Response>(`${Environment.urls.API}/core/token/validate/issu/`)
        .pipe(map(bool => <boolean>bool.data), shareReplay(1));
    }
    return this.cachedIsAdmin$;
  }

  directUserIsChair(eventRaw: number | string): Observable<boolean> {
    // Normaliza tipo (callers passam ora number ora string vinda de route param).
    const event = typeof eventRaw === 'string' ? parseInt(eventRaw, 10) : eventRaw;
    if (!this.cachedIsChair.has(event)) {
      const obs$ = this.http.get<Response>(`${Environment.urls.API}/core/event/${event}/peopleIsChair/`)
        .pipe(map(bool => <boolean>bool.data), shareReplay(1));
      this.cachedIsChair.set(event, obs$);
    }
    return this.cachedIsChair.get(event)!;
  }

  directUserIsUserEvent(eventRaw: number | string, role: UserEventRole): Observable<boolean> {
    const event = typeof eventRaw === 'string' ? parseInt(eventRaw, 10) : eventRaw;
    const key = `${event}|${role}`;
    if (!this.cachedIsUserEvent.has(key)) {
      const obs$ = this.http.get<Response>(`${Environment.urls.API}/core/event/${event}/userIsChairOrCommittee/`, {
        params: { role }
      }).pipe(map(bool => <boolean>bool.data), shareReplay(1));
      this.cachedIsUserEvent.set(key, obs$);
    }
    return this.cachedIsUserEvent.get(key)!;
  }

  userIsAdminOrChair(event: number): Observable<boolean> {
    // Return True if user is Chair or SuperUser
    return this.directUserIsChair(event);
  }

  userIsCommittee(event: number): Observable<boolean> {
    return this.directUserIsUserEvent(event, UserEventRole.COMMITTEE);
  }

  userIsAdminOrChairOrCommittee(event: number): Observable<boolean> {
    return forkJoin([this.directUserIsAdmin(), this.directUserIsUserEvent(event, UserEventRole.CHAIR), this.directUserIsUserEvent(event, UserEventRole.COMMITTEE)])
      .pipe(map(
        ([isAdmin, isChair, isCommittee]) => isAdmin || isChair || isCommittee
      ));
  }

  getLoginHistory(user: number): Observable<Array<LoginHistory>> {
    return this.http
      .get<Response>(`${Environment.urls.API}/core/auth/user/loginHistory/${user}/`)
      .pipe(map(v => plainToClass(LoginHistory, v.data as Array<LoginHistory>)));
  }

  getSessionId(): Observable<Response> {
    return this.http
      .get<Response>(`${Environment.urls.API}/core/auth/user/sessionId/`);
  }

  hideEmail(email: string): string {
    return email?.replace(/(\w{3})[\w.-]+@([\w.]+\w)/, "$1***@$2")
  }

  validateCaptcha(token: string): Observable<Response> {
    return this.http.post<Response>(`${Environment.urls.API}/core/validate/captcha/`, { token });
  }
}
